import React from 'react';
import { X } from 'lucide-react';
import { colors, typography, radius, spacing } from '../../../theme';
import { parsePhotoType } from '../../../services/checkInService';

/**
 * Individual check-in detail — weight, photos, client note, and (when
 * present) a Coach Review block. coachReview is 0 or 1 coach_notes row
 * matched by check_in_id (migration 052) — rendered only when present, no
 * empty-state placeholder.
 * props: { checkIn, coachReview, onClose }
 */
const CheckInDetail = ({ checkIn, coachReview, onClose }) => {
  if (!checkIn) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={{ ...typography.sectionHeading, color: colors.text }}>
            {new Date(checkIn.check_in_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} Check-In
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={18} color={colors.textMuted} />
          </button>
        </div>

        {checkIn.bodyweight_kg && (
          <div style={{ ...typography.metric, color: colors.text, marginBottom: spacing.xs }}>
            {checkIn.bodyweight_kg} kg
          </div>
        )}
        <div style={{ ...typography.caption, color: colors.textMuted, marginBottom: spacing.lg }}>
          Body weight
        </div>

        {checkIn.photo_urls?.length > 0 && (
          <div style={{ marginBottom: spacing.lg }}>
            <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.sm }}>
              PROGRESS PHOTOS
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
              {checkIn.photo_urls.map((url, idx) => {
                const type = parsePhotoType(url);
                return (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <img src={url} alt={type || `Progress ${idx + 1}`} style={styles.photo} />
                    {type && (
                      <span style={{ ...typography.caption, color: colors.textSubtle, textAlign: 'center', textTransform: 'capitalize' }}>
                        {type}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {checkIn.message_to_coach && (
          <div style={{ marginBottom: spacing.lg }}>
            <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.sm }}>
              CLIENT NOTE
            </div>
            <div style={{ ...typography.body, color: colors.text, lineHeight: 1.6 }}>
              "{checkIn.message_to_coach}"
            </div>
          </div>
        )}

        {coachReview && (
          <div
            style={{
              padding: spacing.md,
              background: colors.goldDim,
              border: `1px solid ${colors.gold}`,
              borderRadius: radius.sm,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.xs }}>
              <span style={{ ...typography.label, color: colors.gold }}>COACH REVIEW</span>
              <span style={{ ...typography.caption, color: colors.textMuted }}>
                {new Date(coachReview.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div style={{ ...typography.body, color: colors.text, lineHeight: 1.6 }}>
              {coachReview.message}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 1000,
  },
  sheet: {
    width: '100%', maxWidth: '480px', maxHeight: '85vh',
    background: colors.surface1,
    borderTopLeftRadius: radius.modal, borderTopRightRadius: radius.modal,
    padding: spacing.lg,
    overflowY: 'auto',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: spacing.lg,
  },
  closeBtn: {
    background: colors.surface2, border: 'none', borderRadius: '50%',
    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
  },
  photo: {
    width: '96px', height: '120px', objectFit: 'cover', borderRadius: radius.sm,
    border: `1px solid ${colors.border}`,
  },
};

export default CheckInDetail;
